import { Fragment, lazy, Suspense, useContext, useState } from "react";
import { Fab, GridLegacy as Grid } from "@mui/material";
import { useHistory } from "react-router-dom";
import { FiCheck } from "react-icons/fi";

import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";

import { api, Flight, FlightDirection, IBookingCreate, RemarkType, RequestStatus, SystemProfile } from "../../../logic/api";

import flex from "../../../styles/flex";

import Scaler from "../../dash/components/scaler/Scaler";
import DashPage from "../../dash/DashPage";

import styles from "../styles";

import usePaths from "../../../utility/paths";
import { SessionContext } from "../../../utility/useSession";

import BookingFields from "../BookingFields/BookingFields";
import AnimateFrom from "../../../components/animations/AnimateFrom";
import PackagePicker from "../../../components/pickers/packagePicker/PackagePickerDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { useTranslation } from "react-i18next";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import AccessDeniedMessage from "@/components/errors/AccessDeniedMessage";
import { usePreferences } from "@/utility/usePreferences";

const BookingRemarks = lazy(() => import("../BookingView/components/BookingRemarks"));
const BookingRemarksV2 = lazy(() => import("../BookingView/components/BookingRemarksV2"));

const BookingCreatePage = () => {
  const paths = usePaths();
  const history = useHistory();
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();
  const [remarks, setRemarks] = useState("");
  const [remarkType, setRemarkType] = useState<RemarkType>(RemarkType.Internal);

  const additionalCustomizableFieldsFlag = useReleaseManagement(ReleaseFlag.releaseAdditionalCustomizableFields);
  const bookingRemarksV2Released = useReleaseManagement(ReleaseFlag.releaseBookingRemarksV2);
  const isStoreFuelTypeReleased = useReleaseManagement(ReleaseFlag.releaseStoreFuelType);
  const isFlightRecordOptionsReleased = useReleaseManagement(ReleaseFlag.releaseFlightRecordOptions);
  const isImproveContactRecordOptionsReleased = useReleaseManagement(ReleaseFlag.releaseImproveContactRecordOptions);
  const isAddLuggageFieldToBookingReleased = useReleaseManagement(ReleaseFlag.releaseAddLuggageFieldToBooking);
  const isCollectionDeliveryPointsReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryPoints);
  const isDisplayCleanAirComplianceReleased = useReleaseManagement(ReleaseFlag.releaseDisplayCleanAirCompliance);
  const { defaultBookingDuration } = usePreferences();

  const now = new Date();

  const [validationErr, setValidationErr] = useState<Error | null>(null);

  const session = useContext(SessionContext);
  const defaultPaxCount = session.current.site?.preferences.contactRecordOptions?.properties?.passengers?.default ?? 1;

  const [booking, setBooking] = useState<IBookingCreate>(({
    isWalkIn: true,
    bookingStartDate: new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), Math.round(now.getMinutes() / 5) * 5),
    leadContact: {
      title: "",
      initial: "",
      lastName: "",
      email: "",
      phoneMobile: ""
    },
    vehicle: {
      registration: "",
      colour: "",
      make: "",
      model: ""
    },
    outboundFlight: {
      direction: FlightDirection.outbound,
      flightNumber: ""
    } as Flight,
    returnFlight: {
      direction: FlightDirection.outbound,
      flightNumber: ""
    } as Flight,
    paxCount: defaultPaxCount,
    vip: false,
    assistance: false
  } as unknown) as IBookingCreate);

  const createBooking = api.hooks.booking.create();

  const [showPackageSelector, setShowPackageSelector] = useState(false);

  const submitBooking = () => {
    //Validate fields
    //TODO

    //const frd = new Date(booking.returnFlight.flightDate);

    let system_profile: SystemProfile = SystemProfile.airportParking;

    if (session != null) {
      if (session.current != null) {
        if (session.current.site != null) {
          if (session.current.site.preferences.system_profile != null) {
            system_profile = session.current.site.preferences.system_profile;
          }
        }
      }
    }
    if (system_profile == SystemProfile.fleetManagement) {
      if (booking.vehicle.vin == null || booking.vehicle.vin.length < 17) {
        setValidationErr(new Error(t("input.validations.invalidVin")));
        return;
      }
      if (booking.vehicle.registration == null || booking.vehicle.registration.length == 0) {
        setValidationErr(new Error(t("input.validations.noVehicleRegistration")));
        return;
      }

    } else {
      const hasBookingEndDate = Boolean(booking.bookingEndDate);
      const hasReturnFlightDate = Boolean(booking.returnFlight?.flightDate);

      if (!hasBookingEndDate && defaultBookingDuration == null && !hasReturnFlightDate) {
        setValidationErr(new Error(t("input.validations.dateTime")));
        return;
      }
      const contactRecordOptions = session.current.site?.preferences.contactRecordOptions;
      const bookingRecordOptions = session.current.site?.preferences.bookingRecordOptions;

      // Title
      if (
        isImproveContactRecordOptionsReleased &&
        contactRecordOptions?.properties?.title?.required &&
        !booking.leadContact.title
      ) {
        setValidationErr(new Error(t("input.validations.noTitle")));
        return;
      }

      // Last name (required by default)
      if (!booking.leadContact.lastName?.trim()) {
        setValidationErr(new Error(t("input.validations.contactSurname")));
        return;
      }

      // First name / initial
      if (
        isImproveContactRecordOptionsReleased &&
        (contactRecordOptions?.properties?.firstName?.visible ?? true) &&
        Boolean(contactRecordOptions?.properties?.firstName?.required)
      ) {
        const displayedValue = session.current.site?.preferences.booking_leadContact_firstName_enabled
          ? booking.leadContact.firstName
          : booking.leadContact.initial;

        if (!displayedValue) {
          setValidationErr(
            new Error(
              t(
                session.current.site?.preferences.booking_leadContact_firstName_enabled
                  ? "input.validations.noFirstName"
                  : "input.validations.noInitial"
              )
            )
          );
          return;
        }
      }

      if (
        booking.leadContact.accountNumber &&
        (
          booking.leadContact.accountNumber.length < 3 ||
          booking.leadContact.accountNumber.length > 99
        )
      ) {
        setValidationErr(new Error(t("input.validations.accountNumber")));
        return;
      }

      if (
        contactRecordOptions?.properties?.passengers?.required &&
        (booking.paxCount === null ||
          booking.paxCount === undefined ||
          Number.isNaN(booking.paxCount) ||
          booking.paxCount < 1)
      ) {
        setValidationErr(new Error(t("input.validations.passengerCountInvalid")));
        return;
      }

      if (additionalCustomizableFieldsFlag || isImproveContactRecordOptionsReleased) {
        if (contactRecordOptions?.properties?.email?.required && !booking.leadContact.email?.trim()) {
          setValidationErr(new Error(t("input.validations.noEmail")));
          return;
        }

        if (contactRecordOptions?.properties?.mobile?.required && !booking.leadContact.phoneMobile?.trim()) {
          setValidationErr(new Error(t("input.validations.noMobile")));
          return;
        }
      }

      // Luggage
      if (
        isAddLuggageFieldToBookingReleased &&
        bookingRecordOptions?.properties?.luggage?.required &&
        booking.luggage == null
      ) {
        setValidationErr(new Error(t("input.validations.noLuggage")));
        return;
      }

      if (
        isCollectionDeliveryPointsReleased &&
        bookingRecordOptions?.properties?.collectionPoint?.required &&
        !booking.collectionPoint?.trim()
      ) {
        setValidationErr(new Error(t("input.validations.noCollectionPoint")));
        return;
      }

      if (
        isCollectionDeliveryPointsReleased &&
        bookingRecordOptions?.properties?.deliveryPoint?.required &&
        !booking.deliveryPoint?.trim()
      ) {
        setValidationErr(new Error(t("input.validations.noDeliveryPoint")));
        return;
      }

      // Vehicle fields are reported after the lead contact ones, matching the order of the cards on the page.
      if (booking.vehicle.registration == null || booking.vehicle.registration.length == 0) {
        setValidationErr(new Error(t("input.validations.noVehicleRegistration")));
        return;
      }

      const vehicleRecordOptions = session.current.site?.preferences.vehicleRecordOptions;
      if (vehicleRecordOptions?.properties?.model?.required && !booking.vehicle.model) {
        setValidationErr(new Error(t("input.validations.noVehicleModel")));
        return
      }

      if (additionalCustomizableFieldsFlag) {
        if (vehicleRecordOptions?.properties?.make?.required && !booking.vehicle.make) {
          setValidationErr(new Error(t("input.validations.noVehicleMake")));
          return
        }

        if (vehicleRecordOptions?.properties?.colour?.required && !booking.vehicle.colour) {
          setValidationErr(new Error(t("input.validations.noVehicleColour")));
          return
        }
      }

      if (isStoreFuelTypeReleased && vehicleRecordOptions?.properties?.fuelType?.required && (booking.vehicle.fuelType === undefined || booking.vehicle.fuelType === null)) {
        setValidationErr(new Error(t("input.validations.noVehicleFuelType")));
        return
      }

      if (isDisplayCleanAirComplianceReleased && vehicleRecordOptions?.properties?.cleanAirCompliance?.required && (booking.vehicle.cleanAirCompliant === undefined || booking.vehicle.cleanAirCompliant === null)) {
        setValidationErr(new Error(t("input.validations.noVehicleCleanAirCompliance")));
        return
      }

      if (isFlightRecordOptionsReleased) {
        const outboundFlightRecordOptions = session.current.site?.preferences.outboundFlightRecordOptions;
        if (outboundFlightRecordOptions?.properties?.flightNumber?.required && !booking.outboundFlight.flightNumber) {
          setValidationErr(new Error(t("input.validations.noOutboundFlightNumber")));
          return
        }
        if (outboundFlightRecordOptions?.properties?.destination?.required && !booking.outboundFlight.flightDestination) {
          setValidationErr(new Error(t("input.validations.noOutboundDestination")));
          return
        }
        if (outboundFlightRecordOptions?.properties?.terminal?.required && !booking.outboundFlight.terminal) {
          setValidationErr(new Error(t("input.validations.noOutboundTerminal")));
          return
        }
      }

      const returnFlightRecordOptions = session.current.site?.preferences.returnFlightRecordOptions;
      if (returnFlightRecordOptions?.properties?.flightNumber?.required && !booking.returnFlight.flightNumber) {
        setValidationErr(new Error(t("input.validations.noReturnFlight")));
        return
      }
      if (isFlightRecordOptionsReleased) {
        if (returnFlightRecordOptions?.properties?.returningFrom?.required && !booking.returnFlight.departureAirport) {
          setValidationErr(new Error(t("input.validations.noReturnFlightFrom")));
          return
        }
        if (returnFlightRecordOptions?.properties?.returnTerminal?.required && !booking.returnFlight.terminal) {
          setValidationErr(new Error(t("input.validations.noReturnTerminal")));
          return
        }
      }
      if (bookingRecordOptions?.properties?.altRef?.required && !booking.altRef) {
        setValidationErr(new Error(t("input.validations.noAltRef")));
        return;
      }
    }

    /*
    if (isInvalidDate(frd) == true) {
        setValidationErr(new Error("No date selected for return flight"));
        return;
    }

    if (booking.returnFlight.terminal == null || booking.returnFlight.terminal.id == null) {
        setValidationErr(new Error("No terminal selected for return flight"));
        return;
    }
    */

    //Show package selector.
    setShowPackageSelector(true);
  }

  const commitBooking = async (packageCode: string, packageRef: string | null) => {
    return new Promise<boolean>((accept) => {
      createBooking.send(session.current.context, {
        ...booking,
        remarksInitial: remarks,
        remarkType,
        packageCode,
        packageRef
      }, (result) => {
        if (result != null && result.id != null) {
          // TODO: DD - look at improving this `history.push(paths.booking(result.id), { createdBooking: result });`
          history.push(paths.booking(result.id));
          accept(true);
          return;
        }
        accept(false);
      });
    })
  }

  const saveBooking = async (data: IBookingCreate) => {
    setBooking(data);
    return true;
  }


  if (!hasPermission("bookingManagement.booking.create")) {
    return (
      <DashPage>
        <AccessDeniedMessage />
      </DashPage>
    );
  }

  return (
    <DashPage>
      <Fragment>
        <PackagePicker
          show={showPackageSelector}
          booking={booking}
          onClose={(pkg) => {
            setShowPackageSelector(false);
            if (pkg) {
              commitBooking(pkg.code, pkg.ref);
            }
          }}>
        </PackagePicker>

        <ErrorDialog err={createBooking.err}></ErrorDialog>
        <ErrorDialog err={validationErr}></ErrorDialog>
        <div style={styles.wrapper}>
          <div style={styles.container}>
            <div style={styles.toolBar}>
              <div style={flex.flexRows}>
                <div style={flex.flexFill}>
                  <Scaler minScale={0.4}>
                    <AnimateFrom delay={0.3} style={{
                      opacity: 0,
                      y: -50
                    }}>  <h1 style={styles.title}>{t("booking.create.title")}</h1></AnimateFrom>

                  </Scaler>

                </div>
              </div>
            </div>

            <AnimateFrom delay={0.3} style={{
              opacity: 0,
              y: 50
            }}>

              <Grid container>
                <BookingFields isNew={true} readOnly={false} booking={booking} saveBooking={(data) => saveBooking(data as IBookingCreate)} />

                {bookingRemarksV2Released ? (
                  // TODO: Make the fallback a dedicated spinner component
                  (<Suspense fallback={<ProgressDialog status={RequestStatus.loading} />}>
                    <BookingRemarksV2
                      isNew={true}
                      booking={booking}
                      onChange={(value) => {
                        setRemarks(value);
                      }}
                      remarkType={remarkType}
                      setRemarkType={(value) => {
                        setRemarkType(value);
                      }}
                    />
                  </Suspense>)
                ) : (
                  <Suspense fallback={<ProgressDialog status={RequestStatus.loading} />}>
                    <BookingRemarks
                      isNew={true}
                      booking={booking}
                      onChange={(value) => {
                        setRemarks(value);
                      }}
                    />
                  </Suspense>
                )}
              </Grid>

            </AnimateFrom>

            <div style={styles.fabPosition}>
              <AnimateFrom delay={1} style={{
                opacity: 0,
                zoom: 5
              }}>
                <Fab style={styles.fab} onClick={() => submitBooking()} data-testid="booking-submit-fab">
                  <FiCheck size={26}></FiCheck>
                </Fab>
              </AnimateFrom>
            </div>


          </div>
        </div>

      </Fragment>
    </DashPage >
  );
}

export default BookingCreatePage;
